import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash, Bank, ArrowDown, ArrowUp } from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { money, fdate } from "@/lib/format";
import { PageHeader, EmptyState, StatCard } from "@/components/common";
import { TextField, SelectField } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const PROPOSITOS = [{ value: "inmobiliaria", label: "Inmobiliaria" }, { value: "propietarios", label: "Propietarios" }, { value: "mixta", label: "Mixta" }];
const CATEGORIAS = [["ingreso", "Ingreso"], ["egreso", "Egreso"], ["renta", "Renta"], ["comision", "Comisión"], ["servicio", "Servicio"], ["mantenimiento", "Mantenimiento"], ["deposito", "Depósito"], ["devolucion_deposito", "Devolución depósito"], ["pago_propietario", "Pago a propietario"], ["nomina", "Nómina"], ["gasto_operativo", "Gasto operativo"], ["transferencia", "Transferencia"]].map(([value, label]) => ({ value, label }));
const catLabel = (v) => CATEGORIAS.find((c) => c.value === v)?.label || v;
const propColor = { inmobiliaria: "text-primary", propietarios: "text-[hsl(var(--accent))]", mixta: "text-foreground" };

const EMPTY_ACC = { nombre: "", banco: "", numero_cuenta: "", clabe: "", tipo: "cheques", proposito: "inmobiliaria", saldo_inicial: "" };
const EMPTY_MOV = { account_id: "", categoria: "ingreso", monto: "", descripcion: "", referencia: "" };

export default function Bancos() {
  const [accounts, setAccounts] = useState([]);
  const [movements, setMovements] = useState([]);
  const [resumen, setResumen] = useState({ totales: {} });
  const [filterAcc, setFilterAcc] = useState("todas");
  const [accOpen, setAccOpen] = useState(false);
  const [movOpen, setMovOpen] = useState(false);
  const [acc, setAcc] = useState(EMPTY_ACC);
  const [editAcc, setEditAcc] = useState(null);
  const [mov, setMov] = useState(EMPTY_MOV);

  const loadMov = useCallback(() => {
    const params = {};
    if (filterAcc && filterAcc !== "todas") params.account_id = filterAcc;
    api.get("/movements", { params }).then((r) => setMovements(r.data));
  }, [filterAcc]);
  const loadAll = () => { api.get("/accounts").then((r) => setAccounts(r.data)); api.get("/bancos/resumen").then((r) => setResumen(r.data)); };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadMov(); }, [loadMov]);

  const saveAcc = async () => {
    if (!acc.nombre) return toast.error("El nombre es obligatorio");
    try {
      const payload = { ...acc, saldo_inicial: parseFloat(acc.saldo_inicial) || 0 };
      if (editAcc) await api.put(`/accounts/${editAcc}`, payload); else await api.post("/accounts", payload);
      toast.success("Cuenta guardada"); setAccOpen(false); loadAll(); loadMov();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delAcc = async (id) => { if (!window.confirm("¿Eliminar cuenta?")) return; await api.delete(`/accounts/${id}`); toast.success("Eliminada"); loadAll(); loadMov(); };

  const saveMov = async () => {
    if (!mov.account_id) return toast.error("Selecciona una cuenta");
    if (!mov.monto || mov.monto <= 0) return toast.error("Monto inválido");
    try {
      await api.post("/movements", { ...mov, monto: parseFloat(mov.monto) });
      toast.success("Movimiento registrado"); setMovOpen(false); setMov(EMPTY_MOV); loadAll(); loadMov();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delMov = async (id) => { if (!window.confirm("¿Eliminar movimiento?")) return; await api.delete(`/movements/${id}`); toast.success("Eliminado"); loadAll(); loadMov(); };

  const t = resumen.totales || {};

  return (
    <div data-testid="bancos-page">
      <PageHeader title="Bancos y Movimientos" subtitle="Fondos de la inmobiliaria separados de los fondos de propietarios" actions={<div className="flex gap-2"><Button variant="outline" data-testid="new-movement-btn" onClick={() => { setMov(EMPTY_MOV); setMovOpen(true); }} className="rounded-sm gap-2"><Plus size={16} /> Movimiento</Button><Button data-testid="new-account-btn" onClick={() => { setAcc(EMPTY_ACC); setEditAcc(null); setAccOpen(true); }} className="rounded-sm gap-2"><Plus size={16} /> Nueva cuenta</Button></div>} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Fondos inmobiliaria" value={money(t.inmobiliaria)} tone="primary" icon={Bank} testId="total-inmobiliaria" />
        <StatCard label="Fondos de propietarios" value={money(t.propietarios)} tone="accent" icon={Bank} testId="total-propietarios" />
        <StatCard label="Cuentas mixtas" value={money(t.mixta)} icon={Bank} />
      </div>

      <Tabs defaultValue="cuentas">
        <TabsList className="rounded-sm">
          <TabsTrigger value="cuentas" data-testid="tab-cuentas">Cuentas</TabsTrigger>
          <TabsTrigger value="movimientos" data-testid="tab-movimientos">Movimientos</TabsTrigger>
        </TabsList>

        <TabsContent value="cuentas" className="mt-4">
          {accounts.length === 0 ? (
            <EmptyState title="Sin cuentas bancarias" hint="Crea cuentas separadas para la inmobiliaria y para los fondos de propietarios." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {accounts.map((a) => (
                <div key={a.id} data-testid={`account-card-${a.id}`} className="bg-card border border-border rounded-sm p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-bold">{a.nombre}</div>
                      <div className="text-xs text-muted-foreground">{a.banco || "—"} · {a.tipo}</div>
                    </div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-sm bg-secondary ${propColor[a.proposito]}`}>{a.proposito}</span>
                  </div>
                  <div className="mt-4 text-2xl font-display font-bold tabular-nums">{money(a.saldo_actual)}</div>
                  <div className="text-xs text-muted-foreground">CLABE {a.clabe || "—"}</div>
                  <div className="flex justify-end gap-1 mt-2">
                    <Button variant="ghost" size="icon" className="rounded-sm" onClick={() => { setAcc({ ...EMPTY_ACC, ...a }); setEditAcc(a.id); setAccOpen(true); }}>✎</Button>
                    <Button variant="ghost" size="icon" className="rounded-sm text-red-600" data-testid={`del-account-${a.id}`} onClick={() => delAcc(a.id)}><Trash size={16} /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="movimientos" className="mt-4">
          <div className="w-56 mb-4">
            <SelectField label="Filtrar por cuenta" value={filterAcc} onChange={setFilterAcc} testId="filter-account" options={[{ value: "todas", label: "Todas las cuentas" }, ...accounts.map((a) => ({ value: a.id, label: a.nombre }))]} />
          </div>
          {movements.length === 0 ? (
            <EmptyState title="Sin movimientos" hint="Los pagos de renta y transferencias con cuenta seleccionada se registran aquí automáticamente." />
          ) : (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Fecha</TableHead><TableHead>Cuenta</TableHead><TableHead>Categoría</TableHead><TableHead>Descripción</TableHead><TableHead>Origen</TableHead><TableHead className="text-right">Monto</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {movements.map((m) => (
                    <TableRow key={m.id} data-testid={`movement-row-${m.id}`}>
                      <TableCell className="text-xs">{fdate(m.fecha)}</TableCell>
                      <TableCell>{m.account_nombre}</TableCell>
                      <TableCell>{catLabel(m.categoria)}</TableCell>
                      <TableCell>{m.descripcion || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{m.origen || "manual"}</TableCell>
                      <TableCell className={`text-right tabular-nums font-semibold ${m.flujo === "ingreso" ? "text-emerald-600" : "text-red-600"}`}>
                        <span className="inline-flex items-center gap-1 justify-end">{m.flujo === "ingreso" ? <ArrowDown size={12} /> : <ArrowUp size={12} />}{money(m.monto)}</span>
                      </TableCell>
                      <TableCell className="text-right"><Button variant="ghost" size="icon" className="rounded-sm text-red-600" onClick={() => delMov(m.id)}><Trash size={16} /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={accOpen} onOpenChange={setAccOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>{editAcc ? "Editar cuenta" : "Nueva cuenta"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <TextField label="Nombre" value={acc.nombre} onChange={(v) => setAcc((a) => ({ ...a, nombre: v }))} testId="account-nombre" col={2} />
            <TextField label="Banco" value={acc.banco} onChange={(v) => setAcc((a) => ({ ...a, banco: v }))} />
            <SelectField label="Tipo" value={acc.tipo} onChange={(v) => setAcc((a) => ({ ...a, tipo: v }))} options={[{ value: "cheques", label: "Cheques" }, { value: "ahorro", label: "Ahorro" }, { value: "inversion", label: "Inversión" }]} />
            <TextField label="Número de cuenta" value={acc.numero_cuenta} onChange={(v) => setAcc((a) => ({ ...a, numero_cuenta: v }))} />
            <TextField label="CLABE" value={acc.clabe} onChange={(v) => setAcc((a) => ({ ...a, clabe: v }))} />
            <SelectField label="Propósito" value={acc.proposito} onChange={(v) => setAcc((a) => ({ ...a, proposito: v }))} testId="account-proposito" options={PROPOSITOS} />
            <TextField label="Saldo inicial" type="number" value={acc.saldo_inicial} onChange={(v) => setAcc((a) => ({ ...a, saldo_inicial: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setAccOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-account-btn" onClick={saveAcc}>Guardar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={movOpen} onOpenChange={setMovOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader><DialogTitle>Registrar movimiento</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <SelectField label="Cuenta" value={mov.account_id} onChange={(v) => setMov((m) => ({ ...m, account_id: v }))} testId="movement-account" options={accounts.map((a) => ({ value: a.id, label: `${a.nombre} (${a.proposito})` }))} />
            <SelectField label="Categoría" value={mov.categoria} onChange={(v) => setMov((m) => ({ ...m, categoria: v }))} testId="movement-categoria" options={CATEGORIAS} />
            <TextField label="Monto" type="number" value={mov.monto} onChange={(v) => setMov((m) => ({ ...m, monto: v }))} testId="movement-monto" />
            <TextField label="Descripción" value={mov.descripcion} onChange={(v) => setMov((m) => ({ ...m, descripcion: v }))} />
            <TextField label="Referencia" value={mov.referencia} onChange={(v) => setMov((m) => ({ ...m, referencia: v }))} />
          </div>
          <DialogFooter><Button variant="outline" className="rounded-sm" onClick={() => setMovOpen(false)}>Cancelar</Button><Button className="rounded-sm" data-testid="save-movement-btn" onClick={saveMov}>Registrar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
